import { useState, useEffect, createContext } from "react";
import { ConsoleLog } from "@inntechcorp/it-design";

import { ErrorDetailProps } from "Api/hooks/usePostFetch";
import { GetLocalState } from "Connectivity/LocalStorage";
import useAPI from "Utils/api/useAPI";
import { PrivateSocketBalanceProps, PrivateSocketContextProps, PrivateSocketGameSessionProps } from "Types/PrivateSocketContextProps";

const PrivateSocketContext = createContext<PrivateSocketContextProps>({} as PrivateSocketContextProps);
      PrivateSocketContext.displayName = "PrivateSocketContext";

export const PrivateSocketProvider = ({ children }:{children:any}) => {

  const [balance, SetBalance] = useState<PrivateSocketBalanceProps>({
    activeBalance     : "0.00",
    casinoBonus       : "0.00",
    free              : "0.00",
    reservedWithdraw  : "0.00",
    sportBonus        : "0.00",
    totalBalance      : "0.00",
    totalBonus        : "0.00",
    fetched           : false
  });

  const [gameSession, SetGameSession] = useState<PrivateSocketGameSessionProps>({
    creditToUser      : "0.00", 
    currencyCode      : "EUR", 
    debitFromUser     : "0.00", 
    duration          : "00:00:00",
    gameCount         : 0, 
    startDate         : new Date()
  });

  const [wsURL,       SetWsURL]       = useState("");
  const [name,        SetName]        = useState("");
  const [token,       SetToken]       = useState("");
  const [room,        SetRoom]        = useState("");
  const [connection,  SetConnection]  = useState<any | null>(null);
  
  /**
   * 0 - not connected
   * 1 - connecting
   * 2 - connected
   * 3 - was reconnected
   * 4 - disconnected
   * 5 - socket error
   */
  const [status, SetStatus] = useState(0);

  const { _fetch, _cancel } = useAPI();

  useEffect(() => {

    if (token === "") return;
    ConsoleLog.single("📡 [Private Socket] Token received, initializing...");
    load();

  }, [token]);

  const listenPrivateSocket = (
    wsURL = GetLocalState("settings").socket.Path,
    name  = GetLocalState("settings").socket.Namespace, 
    room  = "test-client_private", 
    token = ""
  ) => {


    if ( window.location.hostname === "localhost" ) {
      wsURL   = wsURL.replace("[[SITE_URL]]", "staging-site.inntech.ae");
      room    = "test-client_private";
    }
    else {
      wsURL   = wsURL.replace("[[SITE_URL]]", window.location.hostname);
      room    = GetLocalState("settings").client.slug + GetLocalState("settings").socket.PrivateRoom;
    }

    if(import.meta.env.MODE !== "development") {
      wsURL = "/ws";
    }
    
    SetWsURL(wsURL);
    SetName(name);
    SetRoom(room);
    SetToken(token);

  };

  const cancelPrivateSocket = () => {

    reset();
    SetStatus(0);

  };

  /**
   * ----------------------------------------
   * 
   * ----------------------------------------
   */

  const load = () => {

    ConsoleLog.single("📡 [Private Socket] Initial balance fetching from API...");

    _fetch({
      type    : "post",
      apiPath : `${GetLocalState('settings').api.User}/read/balance`,
      success : (result) => {
        ConsoleLog.single("📡 [Private Socket] Balance received, saving the values...");
        success(result);
      },
      error   : (detail?:ErrorDetailProps) => {
        ConsoleLog.single("📡 [Private Socket] Balance couldn't be retrieved from the API", "error");
        ConsoleLog.double("🔴 Inn.tech Web Error [Private Socket Context]", detail?.detail, "error");
        if ( detail ) console.table(detail);
      }
    });

  }

  const reset = () => {  

    _cancel();

    if ( connection )
      connection.close();

    SetWsURL("");
    SetToken("");
    SetName("");
    SetConnection(null);

  }

  const success = (result:any) => {  

    if ( result.success ) {
    
      const _balance = {
        activeBalance     : parseFloat(result.data.active_balance).toFixed(2),
        casinoBonus       : parseFloat(result.data.casino_bonus_balance).toFixed(2), 
        free              : parseFloat(result.data.free_balance).toFixed(2), 
        reservedWithdraw  : parseFloat(result.data.reserved_withdraw_balance).toFixed(2), 
        sportBonus        : parseFloat(result.data.sport_bonus_balance).toFixed(2), 
        totalBalance      : parseFloat(result.data.total_balance).toFixed(2),
        totalBonus        : parseFloat(result.data.total_bonus_balance).toFixed(2)
      }

      SetBalance({
        ...balance,
        ..._balance,
        fetched:true
      });

      ConsoleLog.single("📡 [Private Socket] Balance successfully saved.");

      listen();

    }
    else {
      ConsoleLog.double("🔴 Inn.tech Web Error [Private Socket Context]", "The balance information could not be retrieved via socket.", "error");
    }

  }

  /**
   * ----------------------------------------
   * 
   * ----------------------------------------
   */

  //const handleConnect = (nsConn:any, msg:any) => {
  const handleConnect = () => {
    
    ConsoleLog.single("📡 [Private Socket] Connecting...");

  }

  //const handleConnected = (nsConn:any, msg:any) => {
  const handleConnected = (nsConn:any) => {

    nsConn.joinRoom(room);

    if(nsConn.conn.wasReconnected()) {
      SetStatus(3);
      ConsoleLog.single("📡 [Private Socket] Reconnected");
    }
    else {
      SetStatus(2);
      ConsoleLog.single("📡 [Private Socket] Connected");
    }

  }

  //const handleDisconnect = (nsConn:any, msg:any) => {
  const handleDisconnect = () => {
    
    ConsoleLog.single("📡 [Private Socket] Disconnected");

    SetStatus(4);
    // try reconnect
    //listen();
  }

  //const handleRoomJoin = (nsConn:any, msg:any) => {
  const handleRoomJoin = () => {
    ConsoleLog.single("📡 [Private Socket] Joining Room...");
  }

  //const handlRoomJoined = (nsConn:any, msg:any) => {
  const handlRoomJoined = () => {
    ConsoleLog.single("📡 [Private Socket] Successfully Joined The Room > " + room);
  }
  
  //const handleRoomLeft = (nsConn:any, msg:any) => {
  const handleRoomLeft = () => {
    ConsoleLog.single("📡 [Private Socket] Room Left");
  }

  //const handleAnyEvent = (nsConn:any, msg:any) => {
  const handleAnyEvent = () => {
    // console.log(msg)
  }

  //const handleStream = (nsConn, msg:any) => {
  const handleStream = () => {
    // console.log(msg.Body);
    // console.log(msg)
  }

  const handleLiveData = (nsConn:any, msg:any) => {
    nsConn;
    ConsoleLog.single("📡 [Private Socket] Live Data Updated");
    console.table(JSON.parse(msg.Body));
  }

  const handleGameSession = (nsConn:any, msg:any) => {

    nsConn;
    //console.log("handleBalance", nsConn);
    ConsoleLog.single("📡 [Private Socket] Game Session Updated");
    console.table(JSON.parse(msg.Body));

    const body = JSON.parse(msg.Body);

    const session = {
      creditToUser    : parseFloat(body.credit_to_user).toFixed(2), 
      currencyCode    : body.currency_code, 
      debitFromUser   : parseFloat(body.debit_from_user).toFixed(2), 
      duration        : body.duration, 
      gameCount       : body.game_count, 
      startDate       : body.start_date
    }

    SetGameSession(session);

  }

  const handleBalance = (nsConn:any, msg:any) => {

    nsConn;
    //console.log("handleBalance", nsConn);
    ConsoleLog.double("📡 [Private Socket] Balance Updated", JSON.parse(msg.Body).free_balance);
    console.table(JSON.parse(msg.Body));
    //console.log(JSON.parse(msg.Body));
  
    const body = JSON.parse(msg.Body);

    const _balance = {
      activeBalance     : parseFloat(body.active_balance).toFixed(2),
      casinoBonus       : parseFloat(body.casino_bonus_balance).toFixed(2), 
      free              : parseFloat(body.free_balance).toFixed(2), 
      reservedWithdraw  : parseFloat(body.reserved_withdraw_balance).toFixed(2), 
      sportBonus        : parseFloat(body.sport_bonus_balance).toFixed(2), 
      totalBalance      : parseFloat(body.total_balance).toFixed(2), 
      totalBonus        : parseFloat(body.total_bonus_balance).toFixed(2)
    }

    SetBalance({
      ...balance,
      ..._balance,
      fetched: true
    });

  }

  async function listen() {
    
    let conn;

    SetStatus(1);
    ConsoleLog.single("📡 [Private Socket] Listening the socket...");
    
    try {
      
      conn = await (window as any).neffos.dial(wsURL, {

        site: {

          _OnNamespaceConnect     : handleConnect,
          _OnNamespaceConnected   : handleConnected,
          _OnNamespaceDisconnect  : handleDisconnect,
          _OnRoomJoin             : handleRoomJoin,
          _OnRoomJoined           : handlRoomJoined,
          _OnRoomLeft             : handleRoomLeft,
          _OnAnyEvent             : handleAnyEvent,
          balance                 : handleBalance,
          game_session            : handleGameSession,
          live_data               : handleLiveData,
          stream                  : handleStream

        }

      },
      {
        reconnect : 500,
        headers: {
          "X-Auth-Token"  : token,
          "X-Client-Slug" : GetLocalState("settings").client.slug
        }
      });
      
      conn.connect(name);
      SetConnection(conn);

    } 
    catch (err) {
      
      SetStatus(5);
      reset();
      ConsoleLog.double("🔴 Inn.tech Web Error [Private Socket Context]", err, "error");

    }

  }

  const getContextValue:PrivateSocketContextProps = {
    balance,
    gameSession,
    status,
    listenPrivateSocket,
    cancelPrivateSocket
  };

  return (

    <PrivateSocketContext.Provider
      value={getContextValue}
    >
      {children}
    </PrivateSocketContext.Provider>
    
  );

};

export default PrivateSocketContext;